import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createLatestRequestGuard } from './latestRequest';
import type { CSSProperties } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  Table,
  Input,
  Button,
  Form,
  Space,
  message,
  Select,
  Popover,
  Popconfirm,
  Modal,
  Spin,
  Tooltip,
  Tree,
  Progress,
  Checkbox,
  Tag,
  Typography,
  Dropdown,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  MoreOutlined,
  CopyOutlined,
  QuestionCircleOutlined,
  ExclamationCircleOutlined,
  DownloadOutlined,
  FileTextOutlined,
  CodeOutlined,
  StopOutlined,
  UnlockOutlined,
  LockFilled,
  RightOutlined,
  ThunderboltOutlined,
  BarChartOutlined,
  StarFilled,
  StarOutlined,
  DeleteOutlined,
  ReloadOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { ResizableTitle, MiddleEllipsis, useResizableColumns } from '@components/ResizableTable';
import TimeRangeChip, {
  resolvePartitionRange,
  type TimeRangeValue,
} from '@components/TimeRangeChip';
import {
  decodeGroupBackendToken, sceneGroupDisplay,
} from '@/constants/sceneGroup';
import {
  findScenePath, convertSceneTreeToTreeForDisplay, countLeafNodes,
} from '@/common/sceneSetTree';
import { renderAlgorithmVersionCell } from '@components/AlgorithmVersionCell';
import { JobStatusTag } from '@components/StatusTag';
import CreateJobModal from '@components/CreateJobModal';
import UpdatePriorityModal, {
  PRIORITY_EDITABLE_STATUSES,
} from '@components/UpdatePriorityModal';
import PriorityTag from '@components/PriorityTag';
import { useVersionTrace } from '@components/VersionTraceModal/useVersionTrace';
import { useCurrentUser } from '@/common/auth/adminAcl';
import { useEnterToSearch } from '@/common/keyboard';
import { completionPercent } from '@/common/progress';
import { jobReportPath, jobReportAvailability } from '@/utils/jobReport';
import {
  getJobList,
  isolateJob,
  unisolateJob,
  setJobSecurityLevel,
  deleteJob,
  softDeleteJob,
  setJobFavorite,
  getPriorityList,
  getStatusList,
  getJobSourceList,
  getJobLogs,
  getJobBuildLogs,
  getJobBazelBuildLog,
  getSceneTree,
  getBazelWorkflow,
  retryBazelJob,
  JobItem,
  JobListParams,
  PriorityOption,
  StatusOption,
  SceneTreeNode,
} from '@apis/simulation';
import { deleteDisabledReason } from './jobDelete';
import { canRetryBazelJob, createBazelRetryController } from './bazelRetry';
import './statusRetry.css';

// 状态列标签统一宽度：容纳「编译失败」+ 重试下拉三角，其余状态与之对齐。
const STATUS_TAG_MIN_WIDTH = 112;


// ── 运行结果列：成功 / 用户失败 / 仿真失败 / 终止 ──────────────────────────────
// 口径（与后端 job_service._task_result_breakdown 对齐）：
//   成功   = task 通过(succeeded) + 不通过(notPass)（仿真本身跑完）
//   用户失败 = coredump
//   仿真失败 = 执行异常(failed) + 系统异常(unknown)
//   终止   = 取消(cancelled)
// 中间态（未开始/排队/执行中）不计入，故四者之和可能 < 总数。
const RUN_RESULT_SEGMENTS: {
  key: keyof Pick<JobItem, 'resultSuccessNum' | 'resultUserFailNum' | 'resultSimFailNum' | 'resultAbortedNum'>;
  label: string;
  /** value>0 时的文字色 */
  color: string;
  /** value>0 时是否给数字加浅色底片（失败类需要醒目） */
  tint?: string;
}[] = [
  { key: 'resultSuccessNum', label: '成功', color: '#16a34a' },
  { key: 'resultUserFailNum', label: '用户失败', color: '#d97706', tint: '#fdf0e1' },
  { key: 'resultSimFailNum', label: '仿真失败', color: '#dc2626', tint: '#fdecec' },
  { key: 'resultAbortedNum', label: '终止', color: '#8c9099' },
];
const USER_FAIL_VISUAL = RUN_RESULT_SEGMENTS.find((s) => s.key === 'resultUserFailNum')!;

// 表头与单元格共用同一套 4 列栅格 → 数字精确落在标签正下方，整列纵向对齐。
// 「成功」列额外带「/总数」，故给更宽的份额。
const RUN_RESULT_GRID: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1.7fr 1fr 1fr 1fr',
  alignItems: 'center',
  width: '100%',
};

function RunResultHeader() {
  return (
    <div style={{ lineHeight: 1.4 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
        运行结果
        <Tooltip
          title={
            <div style={{ fontSize: 12, lineHeight: 1.7 }}>
              <div>成功 = 通过 + 不通过（仿真跑完）</div>
              <div>用户失败 = coredump</div>
              <div>仿真失败 = 执行异常 + 系统异常</div>
              <div>终止 = 取消</div>
            </div>
          }
        >
          <QuestionCircleOutlined style={{ color: '#9b9da3', fontSize: 12, cursor: 'help' }} />
        </Tooltip>
      </div>
      <div style={{ ...RUN_RESULT_GRID, fontSize: 11, fontWeight: 400, color: '#9b9da3', marginTop: 3 }}>
        {RUN_RESULT_SEGMENTS.map((s) => (
          <span key={s.key} style={{ textAlign: 'center' }}>{s.label}</span>
        ))}
      </div>
    </div>
  );
}

function RunResultCell({ record }: { record: JobItem }) {
  const { resultSuccessNum, resultUserFailNum, resultSimFailNum, resultAbortedNum } = record;
  // 后端未回填（任一为 null/undefined）→ 无运行结果数据
  const hasData = [resultSuccessNum, resultUserFailNum, resultSimFailNum, resultAbortedNum]
    .every((v) => typeof v === 'number');
  if (!hasData) {
    return <span style={{ color: '#c4c6cc' }}>-</span>;
  }
  const total = record.totalTaskNum ?? 0;
  return (
    <div style={{ ...RUN_RESULT_GRID, fontVariantNumeric: 'tabular-nums' }}>
      {RUN_RESULT_SEGMENTS.map((seg) => {
        const value = (record[seg.key] as number) ?? 0;
        const isZero = value === 0;
        const isSuccess = seg.key === 'resultSuccessNum';
        const showTint = !isZero && !!seg.tint;
        return (
          <div key={seg.key} style={{ textAlign: 'center' }}>
            <Tooltip title={isSuccess ? `${seg.label} ${value}/${total}（总数）` : seg.label}>
              <span
                style={{
                  display: 'inline-block',
                  minWidth: 22,
                  fontSize: 13,
                  fontWeight: isZero ? 400 : 600,
                  color: isZero ? '#d0d3d9' : seg.color,
                  background: showTint ? seg.tint : 'transparent',
                  borderRadius: 6,
                  padding: showTint ? '1px 8px' : '1px 0',
                  lineHeight: '18px',
                }}
              >
                {value}
                {/* 成功列带「/总数」，总数弱化为背景灰、字号更小，给「N 选自总量」的语境 */}
                {isSuccess && total > 0 && (
                  <span style={{ color: '#c4c6cc', fontWeight: 400, fontSize: 10 }}>/{total}</span>
                )}
              </span>
            </Tooltip>
          </div>
        );
      })}
    </div>
  );
}

// 「只看自己」开关的浏览器记忆 key（'1'=勾选 / '0'=不勾选；无记录默认勾选）
const ONLY_MINE_STORAGE_KEY = 'joblist:onlyMine';

// 「只看收藏」开关的浏览器记忆 key（'1'=勾选 / '0'=不勾选；无记录默认不勾选）
const ONLY_FAVORITE_STORAGE_KEY = 'joblist:onlyFavorite';

interface JobListQueryParams {
  task_type_id?: string;
  task_id?: string;
  job_id?: number;
  job_name?: string;
  priority?: string | number;
  start_time?: string;
  start_time_start?: string;
  start_time_end?: string;
  task_status?: string;
  status?: string | number;
  creator?: string;
  /** 数据源（JobSourceEnum.id）：1=日常仿真 2=daily 仿真 3=cicd 仿真 */
  source?: string | number;
  /**
   * partition_time 范围过滤(左闭右开)。冷热分离 v2 一期:前端时间选择器统一走
   * partition_time,让后端触发 PG 分区 pruning。默认最近 30 天(与后端默认口径 +
   * 物理保留期 3 个月对齐)。
   *
   * 存 TimeRangeValue(模式)而非求值后的字符串:preset 的"跟随现在"意图必须
   * 保留到发请求那一刻(见 loadJobList 里 resolvePartitionRange)才求值,否则 end
   * 会冻结在存入 state 的时刻,创建后自动刷新查不到刚建的任务。null = 不过滤。
   */
  partition_time_range?: TimeRangeValue | null;
  page?: number;
  per_page?: number;
}

export default function JobListPage() {
  const { taskTypeId } = useParams<{ taskTypeId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // sim_type：left-nav v2 通过 query 传入，控制本页要展示哪一类仿真任务。
  // - 'planning'（默认）：现有全部 PNC（规控）逻辑，与旧行为完全一致
  // - 'perception'：感知仿真骨架，后端尚未接入 → 直接渲染占位卡
  // 其他未知值一律回退到 'planning'。
  const simType = useMemo(() => {
    const v = searchParams.get('sim_type');
    return v === 'perception' ? 'perception' : 'planning';
  }, [searchParams]);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<JobItem[]>([]);
  const [total, setTotal] = useState(0);
  /**
   * 任意勾选 2 个 job 进对比: 表格 rowSelection 累积 jobId。
   * 使用 preserveSelectedRowKeys 跨页保留(切换分页勾选不清)。
   * 用户勾第 3 个时给 message 提示"最多 2 个"并拦截,避免歧义(不清空前 2 个)。
   */
  const [selectedJobIds, setSelectedJobIds] = useState<React.Key[]>([]);
  /** 勾选 job 快照(id + name),用于顶部操作栏显示 chip 与「对比」按钮 tooltip */
  const [selectedJobsSnapshot, setSelectedJobsSnapshot] = useState<{ jobId: number; jobName: string; simType?: string }[]>([]);
  /**
   * 已勾选行的仿真类型(取首个有 simType 的勾选行)。未勾选/后端未回填时为 undefined,
   * 此时不做行禁用,可比性交由 handleCompareSelected 与后端 compare 接口兜底。
   */
  const pickedSimType = useMemo(
    () => selectedJobsSnapshot.find((j) => j.simType != null)?.simType,
    [selectedJobsSnapshot],
  );
  const [priorityList, setPriorityList] = useState<PriorityOption[]>([]);
  const [statusList, setStatusList] = useState<StatusOption[]>([]);
  const [sourceList, setSourceList] = useState<StatusOption[]>([]);
  const [queryParams, setQueryParams] = useState<JobListQueryParams>(() => {
    // 分页初值从 URL 读（page/per_page），使从 Task 列表返回(navigate(-1))时
    // 恢复到离开前的页码；缺省 1/20。
    const urlPage = Number(searchParams.get('page'));
    const urlPer = Number(searchParams.get('per_page'));
    // 冷热分离 v2 一期:时间窗口默认最近 30 天,让 PG 分区 pruning 只扫命中
    // 的两周子分区(否则退化到扫所有分区,列表首屏 49s)。用户可用日期控件
    // 调整,最长与物理保留期(3 个月)对齐。
    // 存 preset 模式,发请求时(loadJobList)才求值,end 跟随现在,避免冻结导致
    // 新任务查不到。
    return {
      task_type_id: taskTypeId,
      page: Number.isFinite(urlPage) && urlPage > 0 ? urlPage : 1,
      per_page: Number.isFinite(urlPer) && urlPer > 0 ? urlPer : 20,
      partition_time_range: { mode: 'preset', days: 30 },
    };
  });
  // 当前登录用户：用于把「创建人」筛选默认填为当前用户 email（与后端写入的 creator 对齐）
  const { me, loading: userLoading, hasPerm } = useCurrentUser();
  const currentUserEmail = me?.user?.email || '';
  const canManageSecurity = hasPerm('data:confidential:manage');
  // 「只看自己」开关：浏览器记忆（localStorage），每次进入页面读取。
  // 勾选 → 创建人筛选自动带上当前用户；不勾选 → 不加创建人筛选。
  // 历史默认行为是进入即按自己过滤，故无记录时默认勾选，保持向后兼容。
  const [onlyMine, setOnlyMine] = useState<boolean>(() => {
    const v = localStorage.getItem(ONLY_MINE_STORAGE_KEY);
    return v === null ? true : v === '1';
  });
  // 「只看收藏」开关：浏览器记忆（localStorage），每次进入页面读取。
  // 与「只看自己」独立：后端 onlyFavorite 与 creator 过滤 AND 叠加。
  const [onlyFavorite, setOnlyFavorite] = useState<boolean>(() => {
    try {
      return localStorage.getItem(ONLY_FAVORITE_STORAGE_KEY) === '1';
    } catch (err) {
      console.warn('读取只看收藏偏好失败:', err);
      return false;
    }
  });
  // creator 默认值是否已注入（避免重复设置 + 首次请求等待用户信息就绪）
  const [creatorInitialized, setCreatorInitialized] = useState(false);
  const [logModalVisible, setLogModalVisible] = useState(false);
  const [logContent, setLogContent] = useState<string>('');
  const [logLoading, setLogLoading] = useState(false);
  // 编译日志的完整日志下载链接（仅 logKind='build' 时可能有值，用于「查看完整日志」入口）
  const [logDownloadUrl, setLogDownloadUrl] = useState<string | null>(null);
  // 日志类型：'run' = Job 运行日志；'build' = 编译任务的编译日志（决定弹窗标题与数据源）
  const [logKind, setLogKind] = useState<'run' | 'build'>('run');
  const [currentJobId, setCurrentJobId] = useState<number | null>(null);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  // 复制 job：打开弹窗时携带原 record（弹窗内部回填发起参数）；null 表示全新创建
  const [copyRecord, setCopyRecord] = useState<JobItem | null>(null);
  // 修改优先级弹窗:携带当前 job 记录;null 表示未打开。
  const [priorityRecord, setPriorityRecord] = useState<JobItem | null>(null);
  // 场景树：仅供「场景集合」列回填历史 Job 绑定的路径展示（创建弹窗自行加载自己的树）
  const [sceneTree, setSceneTree] = useState<SceneTreeNode[]>([]);
  // ── 定时列表 / 打版触发已迁到独立路由 /schedule/list、/schedule/release-triggers ──
  // 本页只保留 Job 列表；如需查看/管理定时排程，Sidebar 走「计划任务 → 定时列表」。

  // 加载优先级列表
  const loadPriorityList = useCallback(async () => {
    try {
      const list = await getPriorityList();
      setPriorityList(list);
    } catch (err) {
      console.error('加载优先级列表失败:', err);
    }
  }, []);

  // 加载状态列表
  const loadStatusList = useCallback(async () => {
    try {
      const list = await getStatusList();
      setStatusList(list);
    } catch (err) {
      console.error('加载状态列表失败:', err);
    }
  }, []);

  // 加载数据源（JobSourceEnum）下拉列表
  const loadSourceList = useCallback(async () => {
    try {
      const list = await getJobSourceList();
      setSourceList(list);
    } catch (err) {
      console.error('加载数据源列表失败:', err);
    }
  }, []);

  // 加载任务列表
  // 列表请求序号：只采纳最新一次请求的响应，丢弃过期响应（防乱序覆盖）
  const jobListGuardRef = useRef(createLatestRequestGuard());
  const loadJobList = useCallback(async (throwOnError = false) => {
    const seq = jobListGuardRef.current.begin();
    setLoading(true);
    try {
      // 冷热分离 v2 一期:发请求那一刻才把 partition_time_range 解析成绝对区间。
      // preset 每次现算 [now-N天, now],end 跟随现在——创建任务后 onSuccess 走这里
      // 自动刷新,也能查到刚建的任务(而非读挂载时冻结的旧 end)。
      // 只看收藏与普通列表同一时间窗：默认最近 30 天，用户选了范围就按所选范围。
      const resolvedRange = resolvePartitionRange(queryParams.partition_time_range ?? null);
      const params: JobListParams = {
        jobId: queryParams.job_id,
        jobName: queryParams.job_name,
        priority: queryParams.priority,
        status: queryParams.status,
        creator: queryParams.creator || undefined,
        // 只看收藏：仅在勾选时下发（false 走 undefined 省略，保持 URL 干净）
        onlyFavorite: onlyFavorite || undefined,
        source: queryParams.source,
        // 按左栏 sim_type 分组过滤：PNC 或感知，后端 join task_type 按 sim_type 精确匹配
        simType,
        // 冷热分离 v2 一期:时间范围走 partition_time,触发 PG 分区 pruning
        partitionTimeStart: resolvedRange?.start,
        partitionTimeEnd: resolvedRange?.end,
        pageSize: queryParams.per_page,
        current: queryParams.page,
      };

      const response = await getJobList(params);
      if (!jobListGuardRef.current.isLatest(seq)) {
        return;
      }
      setData(response.tasks);
      setTotal(response.total);
    } catch (err) {
      if (jobListGuardRef.current.isLatest(seq)) {
        console.error('加载任务列表失败:', err);
      } else {
        console.warn('过期的任务列表请求失败（已被更新请求取代）:', err);
      }
      if (throwOnError) {
        throw err;
      }
    } finally {
      if (jobListGuardRef.current.isLatest(seq)) {
        setLoading(false);
      }
    }
    // onlyFavorite 变化 → 本函数重建 → 上方首个拉列表 effect 随依赖重跑，触发刷新
  }, [queryParams, simType, onlyFavorite]);
  // 行内操作（收藏/删除等）可能被 memo 化的列定义闭包捕获，经 ref 始终调用最新的 loadJobList
  const loadJobListRef = useRef(loadJobList);
  useEffect(() => {
    loadJobListRef.current = loadJobList;
  }, [loadJobList]);

  const bazelRetryControllerRef = useRef(createBazelRetryController());
  const handleRetryBazelJob = useCallback((jobId: number) => {
    bazelRetryControllerRef.current.open(jobId, {
      confirm: Modal.confirm,
      submit: retryBazelJob,
      reload: () => loadJobListRef.current(true),
      success: message.success,
      error: (err) => {
        console.error('重试 Bazel 编译失败:', err);
        message.error('重试编译提交或状态刷新失败，请查看提示后重试');
      },
    });
  }, []);

  // 加载场景树：拉全量（含 isHidden=true）作为单一数据源。
  // 两个用途分别处理：
  // - 「场景集合」列回填历史 Job 绑定的路径：直接用全量树，否则隐藏集合下的
  //   id 找不到 path 就会显示「-」，体验不一致。
  // - 创建 Job 的 TreeSelect：在 convertSceneTreeToTreeData 里 prune 掉
  //   isHidden=true 的子树，与「场景管理」前台权限一致——隐藏集合不可被新引用。
  const loadSceneTree = useCallback(async () => {
    try {
      const tree = await getSceneTree(true);
      setSceneTree(tree);
    } catch (err) {
      console.error('加载场景树失败:', err);
    }
  }, []);

  // 初始化加载优先级、状态、数据源列表
  useEffect(() => {
    loadPriorityList();
    loadStatusList();
    loadSourceList();
    loadSceneTree(); // 加载场景树用于显示
  }, [loadPriorityList, loadStatusList, loadSourceList, loadSceneTree]);

  // 用户信息加载完后，把当前用户 email 注入「创建人」筛选 + 表单
  // 仅在首次进入页面时生效；用户中途清空/改写筛选后不再覆盖
  useEffect(() => {
    if (creatorInitialized || userLoading) return;
    // 仅当「只看自己」处于勾选态时，才默认按当前用户过滤
    if (onlyMine && currentUserEmail) {
      form.setFieldsValue({ creator: currentUserEmail });
      setQueryParams(prev => ({ ...prev, creator: currentUserEmail, page: 1 }));
    }
    // 冷热分离 v2 一期:首次 mount 把默认最近 30 天 preset 塞进 chip,
    // 与 queryParams 初始 partition_time_range 保持一致。
    form.setFieldsValue({ partition_time_range: { mode: 'preset', days: 30 } });
    setCreatorInitialized(true);
  }, [userLoading, currentUserEmail, creatorInitialized, onlyMine, form]);

  // 首次拉列表必须等 creator 默认值就绪，避免「先发空查询、再发带 creator 的查询」两次请求
  useEffect(() => {
    if (!creatorInitialized) return;
    loadJobList();
  }, [loadJobList, queryParams, creatorInitialized]);

  // 查询
  const handleSearch = () => {
    const values = form.getFieldsValue();
    const newParams: JobListQueryParams = {
      ...queryParams,
      job_id: values.job_id ? Number(values.job_id) : undefined,
      job_name: values.job_name || undefined,
      priority: values.priority !== undefined && values.priority !== '' ? values.priority : undefined,
      status: values.task_status !== undefined && values.task_status !== '' ? values.task_status : undefined,
      creator: values.creator?.trim() || undefined,
      source: values.source !== undefined && values.source !== '' ? values.source : undefined,
      page: 1,
    };

    // 处理时间范围
    if (values.start_time && values.start_time.length === 2) {
      newParams.start_time_start = values.start_time[0].format('YYYY-MM-DD HH:mm:ss');
      newParams.start_time_end = values.start_time[1].format('YYYY-MM-DD HH:mm:ss');
    }

    // 冷热分离 v2 一期:把 chip 的 TimeRangeValue 原样存进 queryParams(不在此
    // 求值),发请求时(loadJobList)才 resolve。preset 由此每次现算 end 跟随现在;
    // absolute 原样固定;清空 = null(不过滤,后端兜底走默认 30 天)。
    newParams.partition_time_range = values.partition_time_range ?? null;

    setQueryParams(newParams);
  };

  const enterToSearch = useEnterToSearch(handleSearch);

  // 「只看自己」切换：写入 localStorage 记忆，并立即按新状态刷新创建人筛选。
  const handleToggleOnlyMine = (checked: boolean) => {
    setOnlyMine(checked);
    try {
      localStorage.setItem(ONLY_MINE_STORAGE_KEY, checked ? '1' : '0');
    } catch (err) {
      // 隐私模式 / 配额满时 setItem 可能抛错：记忆失效但不影响本次筛选
      console.warn('保存「只看自己」状态失败:', err);
    }
    const nextCreator = checked ? (currentUserEmail || undefined) : undefined;
    form.setFieldsValue({ creator: checked ? (currentUserEmail || '') : '' });
    setQueryParams(prev => ({ ...prev, creator: nextCreator, page: 1 }));
  };

  // 「只看收藏」切换：写入 localStorage 记忆，并回到第一页（与其它筛选动作一致，
  // 避免过滤后命中数变少仍停在原页码看到空表）；刷新由 loadJobList 依赖 onlyFavorite 触发。
  const handleToggleOnlyFavorite = (checked: boolean) => {
    setOnlyFavorite(checked);
    try {
      localStorage.setItem(ONLY_FAVORITE_STORAGE_KEY, checked ? '1' : '0');
    } catch (err) {
      // 隐私模式 / 配额满时 setItem 可能抛错：记忆失效但不影响本次筛选
      console.warn('保存只看收藏偏好失败:', err);
    }
    setQueryParams(prev => ({ ...prev, page: 1 }));
  };

  // 重置：创建人是否预填取决于「只看自己」当前状态（与首次进入页面一致）
  const handleReset = () => {
    form.resetFields();
    const nextCreator = onlyMine ? (currentUserEmail || undefined) : undefined;
    if (nextCreator) {
      form.setFieldsValue({ creator: nextCreator });
    }
    // 冷热分离 v2 一期:重置时把时间选择器 + 查询参数一起回到"最近 30 天"默认。
    form.setFieldsValue({ partition_time_range: { mode: 'preset', days: 30 } });
    setQueryParams({
      task_type_id: taskTypeId,
      page: 1,
      per_page: queryParams.per_page || 20,
      creator: nextCreator,
      partition_time_range: { mode: 'preset', days: 30 },
    });
  };

  // 复制任务：携带原 record 打开创建弹窗（弹窗内部按 record 回填发起参数）
  const handleCopyJob = (record: JobItem) => {
    setCopyRecord(record);
    setCreateModalVisible(true);
  };

  // 打开创建任务弹窗（全新创建）
  const handleCreateTask = () => {
    setCopyRecord(null);
    setCreateModalVisible(true);
  };

  /**
   * 「对比选中」按钮:拿 selectedJobsSnapshot 里的 2 个 job 跳对比页。
   * 感知 Job 去「评估报表」(/perception-report),PNC Job 去「任务对比」独立页。
   * 顺序:较早创建的作为基准方(base_jobId 在 URL path),另一个作为对比方(?compareJobId=)。
   * 若不足 2 个或多于 2 个提前警告;两者 simType 不同时拒绝(后端 compare 同口径校验)。
   */
  const handleCompareSelected = () => {
    if (selectedJobsSnapshot.length !== 2) {
      message.warning(`请勾选 2 个 job 进行对比 (当前 ${selectedJobsSnapshot.length} 个)`);
      return;
    }
    // 可比性按 simType (task_type.sim_type) 判定: 同仿真类型即可,不要求同 task_type。
    // 跨仿真类型(如 PNC vs 感知)指标口径不同,对比无意义;混选时下面的感知分支
    // (every === 'perception') 也不成立,会错误落到 PNC 对比页,故必须先拦。
    // simType 缺失时不拦,交由后端 compare 接口兜底校验。
    const [a, b] = selectedJobsSnapshot;
    if (a.simType != null && b.simType != null && a.simType !== b.simType) {
      message.warning('所选 2 个 job 的仿真类型不同,无法对比');
      return;
    }
    // 按 jobId 大小排序,较小 id (通常更早)当基准方; 用户对哪个当基准无强偏好
    // (进对比页后可切换),这样至少 URL 稳定,同一组 2 个 job 组合永远生成同一链接
    const sorted = [...selectedJobsSnapshot].sort((a, b) => a.jobId - b.jobId);
    const base = sorted[0];
    const cmp = sorted[1];
    // 感知:两个 Job 都是感知类型才走评估报表。jobId=较新(id 大)作「本次」,
    // baseJobId=较早(id 小)作「基线」——与 PNC 对比页 base 在 path 的顺序相反,
    // 因为报表页语义是「本次 vs 基线」,基线必须是更早那个。
    if (selectedJobsSnapshot.every((j) => j.simType === 'perception')) {
      navigate(`/perception-report?jobId=${cmp.jobId}&baseJobId=${base.jobId}`);
      return;
    }
    navigate(`/job-tasks/${base.jobId}?mode=compare&compareJobId=${cmp.jobId}`);
  };

  /** 清空勾选状态(2 处 state 同步) */
  const clearSelectedJobs = () => {
    setSelectedJobIds([]);
    setSelectedJobsSnapshot([]);
  };


  // 隔离任务：调度器保留任务现场与日志，可解除
  const handleIsolate = async (jobId: number) => {
    try {
      await isolateJob(jobId);
      message.success('Job 已隔离');
      loadJobListRef.current();
    } catch (err) {
      console.error('任务隔离失败:', err);
    }
  };

  // 解除任务隔离
  const handleUnisolate = async (jobId: number) => {
    try {
      await unisolateJob(jobId);
      message.success('已解除隔离');
      loadJobListRef.current();
    } catch (err) {
      console.error('解除隔离失败:', err);
    }
  };

  // 设为保密 / 取消保密（同步 stamp 该 Job 的全部 task）
  const handleToggleJobSecurity = async (jobId: number, confidential: boolean) => {
    try {
      const r = await setJobSecurityLevel(jobId, confidential ? 0 : 1);
      message.success(
        confidential
          ? `已取消保密（影响 ${r.stampedTasks} 个任务）`
          : `已设为保密（影响 ${r.stampedTasks} 个任务）`,
      );
      loadJobListRef.current();
    } catch (err) {
      console.error('设置保密失败:', err);
    }
  };

  // 取消任务
  const handleDelete = async (jobId: number) => {
    try {
      await deleteJob(jobId);
      message.success('Job 取消成功');
      loadJobListRef.current();
    } catch (err) {
      console.error('任务取消失败:', err);
    }
  };

  // 收藏 / 取消收藏（幂等）。错误 toast 由 axios 拦截器统一弹出，这里只记录。
  const handleToggleFavorite = async (jobId: number, favorite: boolean) => {
    try {
      await setJobFavorite(jobId, favorite);
      message.success(favorite ? '已收藏' : '已取消收藏');
      loadJobListRef.current();
    } catch (err) {
      console.error('收藏操作失败:', err);
    }
  };

  // 删除任务（软删除）：仅隐藏列表展示，不动状态机；确认后调用。
  const handleSoftDelete = (record: JobItem) => {
    Modal.confirm({
      title: `删除任务 ${record.jobId}？`,
      content: '删除后将从列表中隐藏，且无法再被选为对比任务。',
      okType: 'danger',
      okText: '删除',
      onOk: async () => {
        try {
          await softDeleteJob(record.jobId);
          message.success('已删除');
          loadJobListRef.current();
        } catch (err) {
          // 错误 toast 由 axios 拦截器统一弹出（仅创建者/未终态等），这里只记录
          console.error('删除任务失败:', err);
        }
      },
    });
  };
  // 注：单 Job 行内"刷新状态"按钮已下线，统一在「系统 → 系统参数运维 →
  // 运维操作 · 批量刷新未完成 Job 状态」入口触发，避免单条点点点骚扰运维。

  // 查看日志
  const handleViewLog = async (jobId: number) => {
    setCurrentJobId(jobId);
    setLogKind('run');
    setLogModalVisible(true);
    setLogContent('');
    setLogDownloadUrl(null);
    setLogLoading(true);

    try {
      const logs = await getJobLogs(jobId);
      setLogContent(logs);
    } catch (err) {
      console.error('加载日志失败:', err);
      setLogContent('加载日志失败');
    } finally {
      setLogLoading(false);
    }
  };

  // 查看编译日志（仅编译任务）
  const handleViewBuildLog = async (jobId: number) => {
    setCurrentJobId(jobId);
    setLogKind('build');
    setLogModalVisible(true);
    setLogContent('');
    setLogDownloadUrl(null);
    setLogLoading(true);

    try {
      const { logContent, logDownloadUrl } = await getJobBuildLogs(jobId);
      setLogContent(logContent);
      setLogDownloadUrl(logDownloadUrl ?? null);
    } catch (err) {
      console.error('加载编译日志失败:', err);
      setLogContent('加载编译日志失败');
    } finally {
      setLogLoading(false);
    }
  };

  // 查看 bazel 编译日志（仅 package_bazel 任务）：链接来自编译平台回调
  // ``bazel_build_result.call_back.buildLog``，新标签直接打开；找不到/未回填时
  // 提示「请联系管理员查看」。
  const handleViewBazelBuildLog = async (jobId: number) => {
    try {
      const url = await getJobBazelBuildLog(jobId);
      if (!url) {
        message.warning('未找到编译日志，请联系管理员查看');
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('查询编译日志失败:', err);
      message.error('查询编译日志失败，请联系管理员查看');
    }
  };

  // 查看 Argo Workflow（仅 bazel 编译 job）：取最近一次编译触发记录的 workflowUrl 后新开页
  const handleViewArgoWorkflow = async (jobId: number) => {
    try {
      const info = await getBazelWorkflow(jobId);
      if (info?.workflowUrl) {
        window.open(info.workflowUrl, '_blank', 'noopener,noreferrer');
      } else {
        message.warning('该 Job 暂无 Argo Workflow 链接');
      }
    } catch (err) {
      console.error('获取 Argo Workflow 失败:', err);
    }
  };

  const { columnWidths, setColumnWidths, resizeHeaderCell } = useResizableColumns({
    jobId: 80,
    jobName: 200,
    taskTypeName: 110,
    sourceName: 90,
    algorithmVersion: 120,
    sceneSetIds: 100,
    createTime: 160,
    endTime: 160,
    creator: 90,
    priority: 76,
    progress: 88,
    runResult: 244,
    statusName: 130,
    // 操作列:感知多一个「报表」按钮(复制 / 取消 / 报表 / ⋯),PNC 仍是原三件套
    action: simType === 'perception' ? 290 : 220,
  });

  // 左栏在两个任务列表间切换只改 ?sim_type=,组件不重挂,故列宽初值按不住 simType 变化:
  // 切到感知补宽到 290,切回 PNC 收回 220（含末尾收藏星标）。只在列宽还是另一侧的默认值时切,
  // 用户手动拖过的宽度不覆盖。
  useEffect(() => {
    const wide = 290;
    const narrow = 220;
    const want = simType === 'perception' ? wide : narrow;
    const other = simType === 'perception' ? narrow : wide;
    setColumnWidths((prev) => (prev.action === other ? { ...prev, action: want } : prev));
  }, [simType, setColumnWidths]);

  // 算法版本追溯 Modal 接线（供「算法版本」列 onTrace 打开）
  const { onTrace, modal: versionTraceModal } = useVersionTrace();

  // 表格列定义
  const columns: ColumnsType<JobItem> = [
    {
      title: 'JobID',
      dataIndex: 'jobId',
      key: 'jobId',
      width: columnWidths.jobId,
      align: 'center',
      onHeaderCell: resizeHeaderCell('jobId'),
      render: (text) => text || '-',
    },
    {
      title: 'Job 名称',
      dataIndex: 'jobName',
      key: 'jobName',
      width: columnWidths.jobName,
      align: 'center',
      onHeaderCell: resizeHeaderCell('jobName'),
      render: (text, record) => {
        const display = text || '-';
        const isConfidential = (record.securityLevel ?? 0) > 0;
        return (
          <Link
            to={`/job-tasks/${record.jobId}`}
            style={{ color: '#5e6ad2', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}
          >
            <MiddleEllipsis text={display} />
            {isConfidential && (
              <Tooltip title="保密：仅具备查看权限的角色可见">
                <LockFilled style={{ color: '#cf1322', fontSize: 12, flexShrink: 0 }} />
              </Tooltip>
            )}
          </Link>
        );
      },
    },
    {
      title: '任务类型',
      dataIndex: 'taskTypeName',
      key: 'taskTypeName',
      width: columnWidths.taskTypeName,
      align: 'center',
      onHeaderCell: resizeHeaderCell('taskTypeName'),
      render: (text) => text || '-',
    },
    {
      title: '算法版本',
      dataIndex: 'algorithmVersion',
      key: 'algorithmVersion',
      width: columnWidths.algorithmVersion,
      align: 'center',
      onHeaderCell: resizeHeaderCell('algorithmVersion'),
      render: (_text, record) => renderAlgorithmVersionCell(record, onTrace),
    },
    {
      title: '场景集合',
      dataIndex: 'sceneSetIds',
      key: 'sceneSetIds',
      width: columnWidths.sceneSetIds,
      align: 'center',
      onHeaderCell: resizeHeaderCell('sceneSetIds'),
      render: (text, record) => {
        // 按场景发起：sceneSetIds 存的是场景 id 串（非场景集树节点），不走树解析，直接展示数量
        if (record.sceneSource === 'scene') {
          const sceneCount = (text || '')
            .split(',')
            .map((id: string) => id.trim())
            .filter(Boolean).length;
          return sceneCount > 0 ? `${sceneCount}个场景` : '-';
        }

        // 分组来源两处（并集去重）：
        // 1) sceneSetIds 里混合的 L<id>G:<key> 复合 token（历史兼容）
        // 2) sceneGroupRefs（新写入路径，scene_set_ids varchar(64) 装不下时的托底）
        // 与老版视觉一致：分组直接挂在其父叶子节点下作为虚拟子叶子渲染到 tree 里,
        // 计数与展示都当作场景集处理，Popover 里只有一颗 tree（不再单独列「按分类:」区）。
        const ids: number[] = [];
        const groupsByLeaf = new Map<number, Set<string>>();
        const addGroup = (leafId: number, groupKey: string) => {
          if (!groupsByLeaf.has(leafId)) groupsByLeaf.set(leafId, new Set());
          groupsByLeaf.get(leafId)!.add(groupKey);
        };

        for (const raw of (text || '').split(',')) {
          const t = raw.trim();
          if (!t) continue;
          const asNum = parseInt(t, 10);
          if (!isNaN(asNum) && String(asNum) === t) {
            ids.push(asNum);
            continue;
          }
          const decoded = decodeGroupBackendToken(t);
          if (decoded) addGroup(decoded.leafId, decoded.groupKey);
        }
        for (const ref of record.sceneGroupRefs || []) {
          addGroup(ref.leafId, ref.groupKey);
        }

        // 有分组的叶子 id 也要进 foundIds，确保 tree 显示能落到该叶子上
        for (const leafId of groupsByLeaf.keys()) {
          if (!ids.includes(leafId)) ids.push(leafId);
        }

        if (sceneTree.length === 0) {
          return '-';
        }

        const foundIds = ids.filter((id) => findScenePath(sceneTree, id) !== null);
        if (foundIds.length === 0) {
          return '-';
        }

        // 基础 tree（真实节点结构）
        let treeData = convertSceneTreeToTreeForDisplay(sceneTree, foundIds);
        // 把分组挂成对应叶子的虚拟子节点；每个虚拟子叶子视作 1 个场景集参与计数
        if (groupsByLeaf.size > 0) {
          const attachGroups = (nodes: any[]): any[] =>
            nodes.map((n) => {
              const groups = groupsByLeaf.get(n.key);
              if (groups && groups.size > 0) {
                const virtualChildren = Array.from(groups).map((g) => ({
                  title: sceneGroupDisplay(g),
                  key: `${n.key}:${g}`,
                  isLeaf: true,
                }));
                return { ...n, isLeaf: false, children: virtualChildren };
              }
              if (n.children && n.children.length > 0) {
                return { ...n, children: attachGroups(n.children) };
              }
              return n;
            });
          treeData = attachGroups(treeData);
        }

        const leafNodeCount = countLeafNodes(treeData);
        const displayText = leafNodeCount > 0 ? `${leafNodeCount}个场景集` : '-';

        return (
          <Popover
            title="场景集合"
            content={
              <div style={{ maxHeight: 400, overflow: 'auto', width: 300 }}>
                <Tree
                  treeData={treeData}
                  defaultExpandAll
                  selectedKeys={foundIds.map((id: number) => id.toString())}
                  checkable={false}
                  showLine={{ showLeafIcon: false }}
                />
              </div>
            }
            trigger="click"
            placement="right"
          >
            <a style={{ color: '#5e6ad2', cursor: 'pointer' }}>
              {displayText}
            </a>
          </Popover>
        );
      },
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: columnWidths.createTime,
      align: 'center',
      onHeaderCell: resizeHeaderCell('createTime'),
      render: (text) => {
        if (!text) return '-';
        try {
          const date = new Date(text);
          const formatted = date.toLocaleString('zh-CN');
          return <MiddleEllipsis text={formatted} />;
        } catch {
          return text;
        }
      },
    },
    {
      title: '结束时间',
      dataIndex: 'endTime',
      key: 'endTime',
      width: columnWidths.endTime,
      align: 'center',
      onHeaderCell: resizeHeaderCell('endTime'),
      render: (text) => {
        if (!text) return '-';
        try {
          const date = new Date(text);
          const formatted = date.toLocaleString('zh-CN');
          return <MiddleEllipsis text={formatted} />;
        } catch {
          return text;
        }
      },
    },
    {
      title: '数据源',
      dataIndex: 'sourceName',
      key: 'sourceName',
      width: columnWidths.sourceName,
      align: 'center',
      onHeaderCell: resizeHeaderCell('sourceName'),
      render: (text) => text || '-',
    },
    {
      title: '创建人',
      dataIndex: 'creator',
      key: 'creator',
      width: columnWidths.creator,
      align: 'center',
      onHeaderCell: resizeHeaderCell('creator'),
      render: (text) => {
        if (!text) return '-';
        return <MiddleEllipsis text={text} />;
      },
    },
    {
      title: '优先级',
      dataIndex: 'priorityName',
      key: 'priority',
      width: columnWidths.priority,
      align: 'center',
      onHeaderCell: resizeHeaderCell('priority'),
      render: (_, record) => (
        <PriorityTag priority={record.priority} label={record.priorityName} />
      ),
    },
    {
      title: '进度',
      key: 'progress',
      width: columnWidths.progress,
      align: 'center',
      onHeaderCell: resizeHeaderCell('progress'),
      render: (_, record) => {
        const { totalTaskNum, successTaskNum, failTaskNum } = record;
        if (totalTaskNum === 0 || totalTaskNum === undefined) {
          return <span style={{ color: '#c4c6cc' }}>-</span>;
        }
        const completedNum = successTaskNum + failTaskNum;
        const percent = completionPercent(completedNum, totalTaskNum);
        const done = percent >= 100;
        // 进度只表达「完成度」，颜色单一不刺眼：完成=绿、进行中=品牌蓝；
        // 通过/失败的归因交给右侧「运行结果」列，避免这里再用红环误导。
        const strokeColor = done ? '#52c41a' : '#5e6ad2';

        return (
          <Tooltip title={`已完成 ${completedNum}/${totalTaskNum} · 成功 ${successTaskNum} / 失败 ${failTaskNum}`}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Progress
                type="circle"
                percent={percent}
                size={42}
                strokeWidth={8}
                strokeColor={strokeColor}
                trailColor="#eef0f3"
                format={() => (
                  <span style={{ fontSize: 10.5, fontWeight: 600, color: strokeColor }}>{percent}%</span>
                )}
              />
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: <RunResultHeader />,
      key: 'runResult',
      width: columnWidths.runResult,
      align: 'center',
      onHeaderCell: resizeHeaderCell('runResult'),
      render: (_, record) => <RunResultCell record={record} />,
    },
    {
      title: '状态',
      dataIndex: 'statusName',
      key: 'statusName',
      width: columnWidths.statusName,
      align: 'center',
      onHeaderCell: resizeHeaderCell('statusName'),
      render: (_, record) => {
        if (record.status === 25
          && (record.resultSimFailNum ?? 0) === 0
          && (record.resultUserFailNum ?? 0) > 0) {
          return (
            <Tag
              icon={<ExclamationCircleOutlined />}
              style={{
                marginInlineEnd: 0,
                minWidth: STATUS_TAG_MIN_WIDTH,
                textAlign: 'center',
                background: USER_FAIL_VISUAL.tint,
                color: USER_FAIL_VISUAL.color,
                borderColor: USER_FAIL_VISUAL.color,
              }}
            >
              {record.statusName}
            </Tag>
          );
        }
        if (!canRetryBazelJob(record)) {
          return <JobStatusTag status={record.status} statusName={record.statusName} minWidth={STATUS_TAG_MIN_WIDTH} />;
        }
        // 编译失败可重试：状态标签右侧下拉三角，菜单内发起「重试编译」。
        return (
          <Dropdown
            trigger={['click']}
            menu={{
              items: [{ key: 'retry', icon: <ReloadOutlined />, label: '重试编译' }],
              onClick: () => handleRetryBazelJob(record.jobId),
            }}
          >
            <span className="job-status-retry-trigger">
              <JobStatusTag
                status={record.status}
                statusName={record.statusName}
                minWidth={STATUS_TAG_MIN_WIDTH}
                suffix={(
                  <span className="job-status-retry-trigger__caret">
                    <DownOutlined />
                  </span>
                )}
              />
            </span>
          </Dropdown>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      width: columnWidths.action,
      align: 'center',
      onHeaderCell: resizeHeaderCell('action'),
      render: (_, record) => {
        // 「报表」按钮只有感知 Job 有（见 utils/jobReport）；PNC 不渲染这个按钮。
        const reportPath = jobReportPath(record.jobId, record.simType, simType);
        // 报表统计的是场景跑完的评测结论：未跑完 / 失败的 Job 置灰，点进去只会看到空页
        const report = jobReportAvailability(record.status, record.statusName);
        return (
        <Space>
          <Tooltip title="复制该 Job 的发起参数进入创建页面：默认按包运行；编译任务自动带上编译后的包，切到编译模式则回填原编译选项。">
            <Button size="small" icon={<CopyOutlined />} onClick={() => handleCopyJob(record)}>
              复制
            </Button>
          </Tooltip>
          <Tooltip title="“取消”会停止 Job，然后清理掉 Job 现场、日志。">
            <Popconfirm
              title="确认取消"
              description="确定要取消该 Job 吗？取消后 Job 将停止并清理 Job 现场、日志。"
              onConfirm={() => handleDelete(record.jobId)}
              okText="确定"
              cancelText="取消"
            >
              <Button size="small" danger>
                取消
              </Button>
            </Popconfirm>
          </Tooltip>
          {reportPath && (report.enabled ? (
            <Tooltip title="打开该 Job 的评估报表：各检查项的多 case 指标汇总。">
              <Link to={reportPath}>
                <Button size="small" icon={<BarChartOutlined />}>
                  报表
                </Button>
              </Link>
            </Tooltip>
          ) : (
            /* 置灰态不套 <Link>：Link 的 <a> 会照常响应点击，disabled 按钮拦不住跳转 */
            <Tooltip title={report.reason}>
              <Button size="small" icon={<BarChartOutlined />} disabled>
                报表
              </Button>
            </Tooltip>
          ))}
          {/* 收藏：与 ⋮ 同款带边框图标按钮，放在 ⋮ 前（菜单保持最后）。点击即收藏/取消（幂等） */}
          <Tooltip title={record.isFavorite ? '取消收藏' : '收藏'}>
            <Button
              size="small"
              aria-label={record.isFavorite ? '取消收藏' : '收藏'}
              aria-pressed={!!record.isFavorite}
              icon={record.isFavorite ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />}
              onClick={() => handleToggleFavorite(record.jobId, !record.isFavorite)}
            />
          </Tooltip>
          <Popover
            overlayClassName="job-action-popover"
            content={
              <div className="job-action-menu">
                {record.isolated ? (
                  <Popconfirm
                    title="确认解除隔离"
                    description="确定要解除该 Job 的隔离状态吗？恢复调度平台的正常处理。"
                    onConfirm={() => handleUnisolate(record.jobId)}
                    okText="确定"
                    cancelText="取消"
                  >
                    <div className="job-action-menu__item job-action-menu__item--success">
                      <UnlockOutlined className="job-action-menu__icon" />
                      <span className="job-action-menu__label">解除隔离</span>
                    </div>
                  </Popconfirm>
                ) : (
                  <Popconfirm
                    title="确认隔离"
                    description="确定要隔离该 Job 吗？隔离后 Job 现场与日志将被保留，可通过“解除隔离”恢复。"
                    onConfirm={() => handleIsolate(record.jobId)}
                    okText="确定"
                    cancelText="取消"
                  >
                    <div className="job-action-menu__item job-action-menu__item--danger">
                      <StopOutlined className="job-action-menu__icon" />
                      <span className="job-action-menu__label">隔离</span>
                    </div>
                  </Popconfirm>
                )}

                {canManageSecurity && (
                  (record.securityLevel ?? 0) > 0 ? (
                    <div
                      className="job-action-menu__item"
                      onClick={() => handleToggleJobSecurity(record.jobId, true)}
                    >
                      <UnlockOutlined className="job-action-menu__icon" />
                      <span className="job-action-menu__label">取消保密</span>
                    </div>
                  ) : (
                    <div
                      className="job-action-menu__item job-action-menu__item--danger"
                      onClick={() => handleToggleJobSecurity(record.jobId, false)}
                    >
                      <LockFilled className="job-action-menu__icon" />
                      <span className="job-action-menu__label">设为保密</span>
                    </div>
                  )
                )}

                {(() => {
                  // 修改优先级:仅未开始/排队中/编译中允许。其它状态灰掉 + tooltip。
                  const editable = PRIORITY_EDITABLE_STATUSES.has(record.status);
                  const item = (
                    <div
                      className={`job-action-menu__item${
                        editable ? '' : ' job-action-menu__item--disabled'
                      }`}
                      onClick={() => {
                        if (!editable) return;
                        setPriorityRecord(record);
                      }}
                    >
                      <ThunderboltOutlined className="job-action-menu__icon" />
                      <span className="job-action-menu__label">修改优先级</span>
                      <RightOutlined className="job-action-menu__chevron" />
                    </div>
                  );
                  return editable ? item : (
                    <Tooltip
                      title="当前状态不支持修改优先级(仅未开始 / 排队中 / 编译中)"
                      placement="left"
                    >
                      {item}
                    </Tooltip>
                  );
                })()}

                {(() => {
                  // 删除(软删除):仅创建者 + 终态可删。不满足时灰掉 + tooltip 说明原因。
                  // 口径是后端 soft-delete 的 email 子集;此处只是前置 UX,最终以后端校验为准。
                  const reason = deleteDisabledReason(record, currentUserEmail);
                  const item = (
                    <div
                      className={`job-action-menu__item${
                        reason ? ' job-action-menu__item--disabled' : ' job-action-menu__item--danger'
                      }`}
                      onClick={() => {
                        if (reason) return;
                        handleSoftDelete(record);
                      }}
                    >
                      <DeleteOutlined className="job-action-menu__icon" />
                      <span className="job-action-menu__label">删除</span>
                    </div>
                  );
                  return reason ? (
                    <Tooltip title={reason} placement="left">
                      {item}
                    </Tooltip>
                  ) : (
                    item
                  );
                })()}

                <div
                  className="job-action-menu__item"
                  onClick={() => handleViewLog(record.jobId)}
                >
                  <FileTextOutlined className="job-action-menu__icon" />
                  <span className="job-action-menu__label">运行日志</span>
                  <RightOutlined className="job-action-menu__chevron" />
                </div>

                {record.buildType === 'compile_platform' && (
                  <div
                    className="job-action-menu__item"
                    onClick={() => handleViewBuildLog(record.jobId)}
                  >
                    <CodeOutlined className="job-action-menu__icon" />
                    <span className="job-action-menu__label">编译日志</span>
                    <RightOutlined className="job-action-menu__chevron" />
                  </div>
                )}

                {record.buildType === 'package_bazel' && (
                  <>
                    <div
                      className="job-action-menu__item"
                      onClick={() => handleViewBazelBuildLog(record.jobId)}
                    >
                      <CodeOutlined className="job-action-menu__icon" />
                      <span className="job-action-menu__label">编译日志</span>
                      <RightOutlined className="job-action-menu__chevron" />
                    </div>
                    <div
                      className="job-action-menu__item"
                      onClick={() => handleViewArgoWorkflow(record.jobId)}
                    >
                      <FileTextOutlined className="job-action-menu__icon" />
                      <span className="job-action-menu__label">Argo Workflow</span>
                      <RightOutlined className="job-action-menu__chevron" />
                    </div>
                  </>
                )}

                <div className="job-action-menu__divider" />

                <div className="job-action-menu__meta">
                  <div className="job-action-menu__meta-label">调度任务 ID</div>
                  <div className="job-action-menu__meta-value">
                    <span className="job-action-menu__meta-id" title={record.dispatchJobId || ''}>
                      {record.dispatchJobId || '—'}
                    </span>
                    {record.dispatchJobId && (
                      <Tooltip title="复制调度任务 ID">
                        <span
                          className="job-action-menu__copy"
                          onClick={() => {
                            navigator.clipboard
                              ?.writeText(record.dispatchJobId as string)
                              .then(() => message.success('调度任务 ID 已复制'))
                              .catch(() => message.error('复制失败'));
                          }}
                        >
                          <CopyOutlined />
                        </span>
                      </Tooltip>
                    )}
                  </div>
                </div>
              </div>
            }
            trigger="click"
          >
            <Button size="small" icon={<MoreOutlined />} />
          </Popover>
        </Space>
        );
      },
    },
  ];


  // PNC / 感知复用同一套 UI；仅标题按 sim_type 切文案，数据由后端按 sim_type join task_type 过滤。
  const simTypeTitle = simType === 'perception' ? '感知仿真 · 任务列表' : 'PNC仿真 · 任务列表';

  return (
    <div className="layout-container layout-container--compact">
      <Typography.Title level={5} className="page-title">{simTypeTitle}</Typography.Title>
      {/* 筛选栏 */}
      <div className="layout-filter layout-filter--compact">
        {/* onFinish + 查询按钮 htmlType="submit":输入框内按 Enter 即查询。
            走原生 form 提交而非逐个挂 onPressEnter——后者在中文输入法上屏那次
            Enter 会读到文字上屏前的旧值,原生提交则必然发生在组字结束之后。 */}
        <Form form={form} layout="inline" onFinish={handleSearch}>
          <Form.Item name="job_id" label="JobID">
            <Input placeholder="请输入JobID" allowClear style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="job_name" label="Job 名称">
            <Input placeholder="请输入 Job 名称" allowClear style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="priority" label="优先级">
            <Select
              {...enterToSearch(1)}
              placeholder="请选择优先级"
              allowClear
              style={{ width: 150 }}
            >
              <Select.Option value="">全部</Select.Option>
              {priorityList.map((item, index) => (
                <Select.Option key={item.code ?? item.value ?? index} value={item.value}>
                  {item.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="task_status" label="Job 状态">
            <Select
              {...enterToSearch(2)}
              placeholder="请选择 Job 状态"
              allowClear
              style={{ width: 150 }}
            >
              {statusList.map((item, index) => (
                <Select.Option key={item.code ?? item.value ?? index} value={item.value}>
                  {item.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="source" label="数据源">
            <Select
              {...enterToSearch(3)}
              placeholder="请选择数据源"
              allowClear
              style={{ width: 150 }}
            >
              <Select.Option value="">全部</Select.Option>
              {sourceList.map((item, index) => (
                <Select.Option key={item.code ?? item.value ?? index} value={item.value}>
                  {item.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="creator" label="创建人">
            <Input placeholder="请输入创建人" allowClear style={{ width: 150 }} />
          </Form.Item>
          {/*
            冷热分离 v2 一期:时间选择器改成"chip + Popover"折叠式设计。
            默认收起,chip 上一行文字自解释当前时间窗("最近 30 天"/日期区间);
            点击 chip 展开 Popover,内嵌快捷预设按钮 + RangePicker。
            设计目的:筛选栏视觉密度可控,时间不占宽,但依然一眼看到当前窗口。
          */}
          <Form.Item name="partition_time_range" label="创建时间" style={{ marginBottom: 0 }}>
            <TimeRangeChip />
          </Form.Item>
          <Form.Item>
            <Checkbox
              checked={onlyMine}
              onChange={e => handleToggleOnlyMine(e.target.checked)}
            >
              只看自己
            </Checkbox>
          </Form.Item>
          <Form.Item>
            <Checkbox checked={onlyFavorite} onChange={e => handleToggleOnlyFavorite(e.target.checked)}>
              只看收藏
            </Checkbox>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                查询
              </Button>
              <Button onClick={handleReset}>重置</Button>
              <Button type="primary" onClick={handleCreateTask}>
                创建 Job
              </Button>
              {/*
               * 「对比选中」按钮:仅在勾选恰好 2 个 job 时启用;
               * hover tooltip 显示当前勾选的 2 个 job 名称便于用户确认。
               * 视觉靠拢 test 平台既有主色按钮,不新引色环。
               */}
              <Tooltip
                title={
                  selectedJobsSnapshot.length === 0
                    ? '在下方任务列表勾选 2 个 job 后即可对比'
                    : selectedJobsSnapshot.length === 1
                    ? `已勾选: ${selectedJobsSnapshot[0].jobName};再选 1 个即可对比`
                    : selectedJobsSnapshot.length === 2
                    ? `对比: ${selectedJobsSnapshot[0].jobName}  ↔  ${selectedJobsSnapshot[1].jobName}`
                    : `已勾选 ${selectedJobsSnapshot.length} 个,请只保留 2 个`
                }
              >
                <Button
                  icon={<CopyOutlined />}
                  disabled={selectedJobsSnapshot.length !== 2}
                  onClick={handleCompareSelected}
                >
                  对比选中 {selectedJobsSnapshot.length > 0 ? `(${selectedJobsSnapshot.length}/2)` : ''}
                </Button>
              </Tooltip>
              {selectedJobsSnapshot.length > 0 && (
                <Button size="small" type="text" onClick={clearSelectedJobs}>
                  清空勾选
                </Button>
              )}
            </Space>
          </Form.Item>
        </Form>
      </div>

      {/* 任务列表 */}
      <div className="layout-content">
        <Table
          rowKey="jobId"
          dataSource={data}
          columns={columns}
          loading={loading}
          size="small"
          components={{ header: { cell: ResizableTitle } }}
          tableLayout="fixed"
          scroll={{ x: Object.values(columnWidths).reduce((a, b) => a + b, 0) }}
          /*
           * 勾选 2 个 job 进对比: preserveSelectedRowKeys 跨页保留;
           * 用户勾第 3 个时给 message 提示"最多 2 个",不静默拦截保留前 2 个(旧勾选)。
           * 跨页 selectedJobsSnapshot 需要同步维护,不能只靠 data 里查:切页后 data 不含
           * 上一页勾选的 job,查不到 jobName 会给对比页 URL 塞不出信息。
           */
          rowSelection={{
            type: 'checkbox',
            selectedRowKeys: selectedJobIds,
            preserveSelectedRowKeys: true,
            columnWidth: 48,
            onChange: (keys, rows) => {
              if (keys.length > 2) {
                message.warning('最多勾选 2 个 job 进行对比,请先取消一个');
                return;
              }
              setSelectedJobIds(keys);
              // 合并当前 rows 与旧 snapshot,按最新 keys 保留;
              // rows 只含当前页勾选行,旧页勾选的 job 从 snapshot 保留
              setSelectedJobsSnapshot((prev) => {
                const map = new Map<number, { jobId: number; jobName: string; simType?: string }>();
                for (const p of prev) map.set(p.jobId, p);
                // simType 取自行数据（后端 JobVo.simType），不能用 URL 上的 ?sim_type=——
                // 后者对未知值一律回退 planning，判不准归属。
                for (const r of rows) map.set(r.jobId, { jobId: r.jobId, jobName: r.jobName || `Job ${r.jobId}`, simType: r.simType });
                const keySet = new Set(keys.map(Number));
                return Array.from(map.values()).filter((x) => keySet.has(x.jobId));
                        });
                      },
                      getCheckboxProps: (record) => ({
                        // 禁用未勾行的两种情形,视觉引导「先取消再勾」:
                        // 1) 已勾满 2 个;2) 该行仿真类型与已勾行不同(跨 simType 不可对比)。
                        // simType 缺失时不禁用,交由 handleCompareSelected / 后端兜底。
                        disabled:
                          !selectedJobIds.includes(record.jobId) &&
                          (selectedJobIds.length >= 2 ||
                            (pickedSimType != null &&
                              record.simType != null &&
                              record.simType !== pickedSimType)),
                      }),
                    }}
                    pagination={{
                      current: queryParams.page,
                      pageSize: queryParams.per_page,
                      total: total,
                      showSizeChanger: true,
                      pageSizeOptions: ['10', '20', '50', '100', '1000'],
                      showQuickJumper: true,
                      showTotal: (t) => `总共 ${t} 条`,
                      onChange: (page, pageSize) => {
                        setQueryParams({ ...queryParams, page, per_page: pageSize });
                        // 页码同步到 URL，使从 Task 列表返回时恢复当前页
                        const sp = new URLSearchParams(searchParams);
                        sp.set('page', String(page));
                        sp.set('per_page', String(pageSize));
                        setSearchParams(sp, { replace: true });
                      },
                    }}
                  />
                </div>

      {/* 日志查看 Modal（运行日志 / 编译日志共用，按 logKind 区分标题与空态文案）*/}
      <Modal
        title={`${logKind === 'build' ? '编译日志' : 'Job 日志'} - Job ID: ${currentJobId}`}
        open={logModalVisible}
        onCancel={() => {
          setLogModalVisible(false);
          setLogContent('');
          setLogDownloadUrl(null);
          setCurrentJobId(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setLogModalVisible(false);
            setLogContent('');
            setLogDownloadUrl(null);
            setCurrentJobId(null);
          }}>
            关闭
          </Button>,
        ]}
        width={800}
        style={{ top: 20 }}
      >
        {logKind === 'build' && logDownloadUrl && (
          <div style={{ marginBottom: 12 }}>
            <a
              href={logDownloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <DownloadOutlined />
              查看 / 下载完整编译日志
            </a>
          </div>
        )}
        <Spin spinning={logLoading}>
          <div
            style={{
              maxHeight: '70vh',
              overflow: 'auto',
              backgroundColor: '#18181b',
              color: '#a1a1aa',
              padding: '16px',
              fontFamily: "'JetBrains Mono', Consolas, Monaco, monospace",
              fontSize: '12px',
              lineHeight: '1.6',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              borderRadius: 6,
              border: '1px solid #27272a',
            }}
          >
            {logContent || (logLoading ? '加载中...' : (logKind === 'build' ? '暂无编译日志' : '暂无日志'))}
          </div>
        </Spin>
      </Modal>

      {/* 创建 Job 弹窗（与场景管理「发起任务」共用同一组件） */}
      <CreateJobModal
        open={createModalVisible}
        copyRecord={copyRecord}
        onClose={() => {
          setCreateModalVisible(false);
          setCopyRecord(null);
        }}
        onSuccess={loadJobList}
      />

      {priorityRecord && (
        <UpdatePriorityModal
          open={priorityRecord !== null}
          onClose={() => setPriorityRecord(null)}
          onSuccess={() => loadJobList()}
          job={{
            jobId: priorityRecord.jobId,
            jobName: priorityRecord.jobName,
            priority: priorityRecord.priority,
            priorityName: priorityRecord.priorityName,
            status: priorityRecord.status,
          }}
        />
      )}

      {versionTraceModal}
    </div>
  );
}
